import { atom } from '@reatom/framework'
import { useAtom } from '@reatom/npm-react'
import { AnimatePresence, motion } from 'motion/react'
import { useState } from 'react'
import styled, { css } from 'styled-components'

import { ArrowDown2Icon, ArrowUp2Icon, useHapticFeedback } from '@blade/ui-kit'
import { Network, selectedNetworkAtom } from '@root/entities/network'
import { walletsAtom } from '@root/entities/wallet'
import { DomUtil } from '@root/shared'
import { useBreakpoint } from '@root/shared/hooks/useBreakpoint'
import { mediaMinTablet } from '@root/shared/ui-kit'
import { WalletsSummary } from '@root/widgets/wallet'

import BG_ETH from './assets/bg-evm.inline.svg'
import BG_SOLANA from './assets/bg-solana.inline.svg'
import BG_TON from './assets/bg-ton.inline.svg'
import { SummaryCardOutlet } from './summary-card-outlet'
import { SummaryWalletList } from './summary-wallet-list'

import type { DeviceType } from '@blade/ui-kit'
import type { ClassNameProps } from '@root/shared/react'

const COLLAPSED_HEIGHT_MAP: Record<DeviceType, number | undefined> = {
  mobile: 190,
  tablet: 232,
  desktop: undefined,
}

const hasWalletsAtom = atom(
  (ctx) => ctx.spy(walletsAtom).length > 0,
  'hasWalletsAtom',
)

export const WalletsSummaryCard = ({ className }: ClassNameProps) => {
  const [expanded, setExpanded] = useState(false)
  const [network] = useAtom(selectedNetworkAtom)
  const [hasWallets] = useAtom(hasWalletsAtom)
  const breakpoint = useBreakpoint()

  const haptics = useHapticFeedback()

  const toggleExpanded = () => {
    haptics.impactOccurred('light')
    setExpanded((state) => {
      // eslint-disable-next-line sonarjs/prefer-single-boolean-return
      if (!state && hasWallets) {
        return true
      }
      return false
    })
  }

  return (
    <Root
      className={className}
      animate={{
        height: expanded ? 'auto' : COLLAPSED_HEIGHT_MAP[breakpoint],
      }}
    >
      <Background
        animate={{
          opacity:
            network === Network.Ethereum ||
            network === Network.Bsc ||
            network === Network.Base
              ? 1
              : 0,
        }}
        initial={false}
        $background={BG_ETH}
      />
      <Background
        animate={{ opacity: network === Network.Solana ? 1 : 0 }}
        initial={false}
        $background={BG_SOLANA}
      />
      <Background
        animate={{ opacity: network === Network.Ton ? 1 : 0 }}
        initial={false}
        $background={BG_TON}
      />
      <Content onClick={toggleExpanded}>
        <Outlet />
        <Summary />
        <ChevronContainer>
          {expanded ? <ArrowUp2Icon /> : <ArrowDown2Icon />}
        </ChevronContainer>
      </Content>
      <AnimatePresence>
        {expanded ? (
          <List
            layout
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <SummaryWalletList />
          </List>
        ) : undefined}
      </AnimatePresence>
    </Root>
  )
}

const Root = styled(motion.div)`
  overflow: hidden;
  cursor: pointer;
  background-color: var(--appearance-1);
  position: relative;

  background-position: -296px -384px;
  background-repeat: no-repeat;
  border-radius: 24px;
  max-height: 70vh;
  margin-bottom: 8px;

  display: grid;
  grid-template-rows: auto auto auto minmax(0, 1fr);
`

const Content = styled.div`
  position: relative;
  padding: 8px;

  ${mediaMinTablet(css`
    padding: 16px;
  `)}
`

const List = styled(motion.div)`
  position: relative;
  padding-inline: 12px;
  overflow: hidden;
`

const Background = styled(motion.div)<{
  $background: string
}>`
  background: ${({ $background }) => DomUtil.cssUrl($background)};
  height: 1000px;
  position: absolute;
  background-position: -296px -384px;
  background-repeat: no-repeat;
  top: 0;
  left: 0;
  right: 0;

  ${mediaMinTablet(css`
    background-size: 140%;
    background-position: -155px 600px;
    transform: scaleY(-1);
  `)}
`

const ChevronContainer = styled.div`
  display: grid;
  justify-content: center;
`

const Summary = styled(WalletsSummary)`
  padding-inline: 8px;

  ${mediaMinTablet(css`
    padding-inline: 0;
    grid-template-columns: 1fr 310px;
  `)}
`

const Outlet = styled(SummaryCardOutlet)`
  margin-bottom: 18px;

  ${mediaMinTablet(css`
    height: 66px;
    padding: 9px;
    margin-bottom: 22px;
  `)}
`
